import React, { useState, Fragment, useEffect, useRef } from "react";
import { Table, Row, Col, Card, CardBody, Spinner } from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { showToast } from "../ToastNotification";
import PieChartApexTotal from "../Graphs/PieChartApexTotal";
import CommonLoading from "./CommonLoading";
import { getCurrentFiscalYearDates } from "./WorkingDateRange";
import { useVirtualizer } from '@tanstack/react-virtual';

const TableContainerDateRangeFilter4 = ({
  footer,
  summary,
  columns,
  data,

  expense,
  stockType,
  theadClass = "",
  tbodyClass = "",
  onDateRangeChange,
  dateRange,
  setDateRange,
  globalFilter,
  setGlobalFilter
}) => {
  
  const [summaryData, setSummarydata] = useState(summary);
  const [sorting, setSorting] = useState([]);
  const { fyStart, fyEnd } = getCurrentFiscalYearDates();
  
  const [tableLoading, setTableLoading] = useState(false);

  const convertToDateFormat = (dateString) => {
    const months = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };

    const [day, month, year] = dateString.replace(",", "").split(" ");
    return `${day.padStart(2, "0")}-${months[month]}-${year}`;
  };

  // console.log("summary", summary);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
  } = table;

  const initialRange =
    summary?.start && summary?.end
      ? [convertToDateFormat(summary?.start), convertToDateFormat(summary?.end)]
      : [];

  // const parseDate = (dateString) => {
  //   if (!dateString) return null;
  //   const [day, month, year] = dateString.split("-").map(Number);
  //   return new Date(year, month - 1, day); // Month is zero-based in JS Date
  // };

  // const initialRange =
  //   summary?.start && summary?.end
  //     ? [parseDate(summary.start), parseDate(summary.end)]
  //     : [];

  // console.log("initialRange", dateRange, initialRange, summaryData);

  const handleApplyClick = () => {
    // console.log("dateRange", dateRange);
    const filteredArr = dateRange.filter((item) => item !== null);
    function convertToYMD(dateStr) {
      const [day, month, year] = dateStr.split("-");
      return `${year}-${month}-${day}`;
  }
    if (filteredArr?.length > 1) {
      setTableLoading(true);
      if (onDateRangeChange) {
        const formatDate = (date) => {
          return `${date?.getFullYear()}-${String(
            date?.getMonth() + 1
          ).padStart(2, "0")}-${String(date?.getDate()).padStart(2, "0")}`;
        };

        onDateRangeChange({
          start_date: formatDate(dateRange[0]),
          end_date: formatDate(dateRange[1]),
          setTableLoading,
        });
      }
    } else {
      setTableLoading(true);
      onDateRangeChange({
        start_date: convertToYMD(summary?.start),
        end_date: convertToYMD(summary?.end),
        setTableLoading,
      });
      // showToast("Please select a date range to filter", "error");
    }
  };

  //           "total_open_long_term_pnl": 0,
  //           "total_open_short_term_pnl": 60566.95,
  //           "total_open_speculative_pnl": 16358.06,
  //           "open_total_pnl": 76925.01,
  //           "open_total_buy": 6568626.84,
  //           "open_total_sell": 6645551.94,
  //           "open_total_open_pos_val": 0,
  //           "open_total_close_pos_val": 0,
  //           "total_close_long_term_pnl": 0,
  //           "total_close_short_term_pnl": 0,
  //           "total_close_speculative_pnl": 0,
  //           "close_total_pnl": 0,
  //           "close_total_buy": 5903500.99,
  //           "close_total_sell": 0,
  //           "close_total_open_pos_val": 5903486.59,
  //           "close_total_close_pos_val": 0,


  const tableContainerRef = useRef(null);
const rows = table.getRowModel().rows;

const rowVirtualizer = useVirtualizer({
  count: rows.length,
  getScrollElement: () => tableContainerRef.current,
  estimateSize: () => 45,
  overscan: 10,
});

const virtualRows = rowVirtualizer.getVirtualItems();
const totalSize = rowVirtualizer.getTotalSize();

const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
const paddingBottom = virtualRows.length > 0 
  ? totalSize - virtualRows[virtualRows.length - 1].end 
  : 0;

  useEffect(() => {
    if (stockType === "open") {
      const totals = getRowModel()?.rows.reduce(
        (acc, item) => {
          // open
          acc.open_total_buy += +item?.original.buy_value || 0;
          acc.open_total_sell += +item?.original.sell_value || 0;
          acc.total_open_short_term_pnl += +item?.original.short_term_pnl || 0;
          acc.total_open_long_term_pnl += +item?.original.long_term_pnl || 0;
          acc.total_open_speculative_pnl +=
            +item?.original.speculative_pnl || 0;
          acc.open_total_pnl += +item?.original.total_pnl || 0;
          acc.open_total_open_pos_val += +item?.original.open_position_val || 0;
          acc.open_total_close_pos_val +=
            +item?.original.closing_stock_val || 0;

          return acc;
        },
        {
          open_total_buy: 0,
          open_total_sell: 0,
          total_open_short_term_pnl: 0,
          total_open_long_term_pnl: 0,
          total_open_speculative_pnl: 0,
          open_total_pnl: 0,
          open_total_open_pos_val: 0,
          open_total_close_pos_val: 0,
          start: summary?.start,
          end: summary?.end,
        }
      );
      setSummarydata(totals);
    }
    // else if (stockType === "close") {
    //   const totals = getRowModel()?.rows.reduce(
    //     (acc, item) => {
    //       // close
    //       acc.close_total_buy += +item?.original.buy_value || 0;
    //       acc.close_total_sell += +item?.original.sell_value || 0;
    //       acc.total_close_short_term_pnl += +item?.original.short_term_pnl || 0;
    //       acc.total_close_long_term_pnl += +item?.original.long_term_pnl || 0;
    //       acc.total_close_speculative_pnl +=
    //         +item?.original.speculative_pnl || 0;
    //       acc.close_total_pnl += +item?.original.total_pnl || 0;
    //       acc.close_total_open_pos_val +=
    //         +item?.original.open_position_val || 0;
    //       acc.close_total_close_pos_val +=
    //         +item?.original.closing_stock_val || 0;
    //       return acc;
    //     },
    //     {
    //       close_total_buy: 0,
    //       close_total_sell: 0,
    //       total_close_short_term_pnl: 0,
    //       total_close_long_term_pnl: 0,
    //       total_close_speculative_pnl: 0,
    //       close_total_pnl: 0,
    //       close_total_open_pos_val: 0,
    //       close_total_close_pos_val: 0,
    //       start: summary?.start,
    //       end: summary?.end,
    //     }
    //   );
    //   setSummarydata(totals);
    // }
  }, [getRowModel, globalFilter, tableLoading]);
  function formatNumber(value) {
    if (typeof value !== "number") return value; // return as is if not a number
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return (
    <Fragment>
      {/* Search and Date Filter */}
      {stockType === "open" && (
        <Row className="">
          {/* <div className="search-box d-inline-block col-xxl-3 col-xl-4 col-lg-7 col-12 mb-2">
         
            <input
              id="search-bar-0"
              type="text"
              style={{
                backgroundColor: "#F1F4F7",
                border: "1px solid transparent",
              }}
              className="form-control border-0 search"
              placeholder="Search by instrument"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            <i className="bx bx-search-alt search-icon"></i>
          </div> */}
          {/* <div className="date-box d-inline-block col-xxl-4 col-xl-4 col-lg-8 col-8">
            <Flatpickr
              style={{
                backgroundColor: "#F1F4F7",
                border: "1px solid transparent",
              }}
              // options={{ mode: "range", dateFormat: "d-m-Y", maxDate: "today" }}
              options={{
                mode: "range",
                dateFormat: "d-m-Y",
                minDate: fyStart,
              maxDate: new Date(Math.min(fyEnd, new Date(new Date().setDate(new Date().getDate() - 1)))),
                onClose: (selectedDates, dateStr, instance) => {
                  if (selectedDates.length === 2) {
                    const diffInDays =
                      (selectedDates[1] - selectedDates[0]) /
                      (1000 * 60 * 60 * 24);
                    if (diffInDays > 89) {
                      instance.clear(); // Clear selection if the range exceeds 7 days
                      // alert("You can only select a maximum range of 7 days.");
                      showToast(
                        "You can only select a maximum range of 90 days.",
                        "error"
                      );
                    }
                  }
                },
              }}
              value={dateRange.length ? dateRange : initialRange}
              onChange={setDateRange}
              className="form-control"
            />
            <i className="bx bx-calendar date-icon"></i>
          </div>
          <div className="col-xxl-2 col-xl-2 col-lg-4 col-4">
            <button
              className="btn btn-primary w-100"
              onClick={handleApplyClick}
            >
              Apply
            </button>
          </div> */}
        </Row>
      )}

      {!tableLoading && stockType === "open" && (
        <Row className="mb-0 m-0 p-0">
          <Col xxl={6} xl={6} md={6} lg={6} sm={12} className="mb-4">
            <Card className="h-100 mb-0">
              <CardBody className="p-0">
                {/* <div className="centerContent my-2">
                  <h3>Gain / Loss</h3>
                </div> */}
                <div className="row m-0 p-0">
                  <div className="col-xxl-6 col-xl-12 col-lg-12 col-md-12 col-12">
                    <PieChartApexTotal
                      dataColors={["#1261A6", "#0FA67B"]}
                      series={[+expense?.net_realised, +expense?.unrealised]}
                      labels={["Net Realised", "Un Realised"]}
                      totalLabel={{ label: "Gain / Loss" }}
                    />
                  </div>
                  <div className="centerContent col-xxl-6 col-xl-12 col-lg-12 col-md-12 col-12">
                    <div className=" w-100 ">
                      <div className="d-flex align-items-center justify-content-between border-bottom">
                        <div className=" d-flex align-items-center justify-content-start">
                          <i class="ri-stop-fill align-middle fs-24 text-primary me-1 mb-0"></i>
                          <h4 className="mb-0 fw-medium"> Net Realised</h4>
                        </div>
                        <h4 className="mb-0 fw-medium">
                          {" "}
                          {parseFloat(expense?.net_realised) < 0 && (
                             <i class="ms-1 ri-arrow-down-line align-middle text-danger"></i>
                          )}
                          ₹
                          {parseFloat(expense?.net_realised).toLocaleString(
                            "en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                              }
                          )}
                        </h4>
                      </div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className=" d-flex align-items-center justify-content-start">
                          <i class="ri-stop-fill align-middle fs-24 text-success me-1 mb-0"></i>
                          <h4 className="mb-0 fw-medium"> Un Realised</h4>
                        </div>
                        <h4 className="mb-0 fw-medium">
                          {" "}
                          {parseFloat(expense?.unrealised) < 0 && (
                            <i class="ms-1 ri-arrow-down-line align-middle text-danger"></i>
                          )}

                          ₹
                          {parseFloat(expense?.unrealised).toLocaleString(
                            "en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                              }
                          )}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xxl={6} xl={6} md={6} lg={6} sm={12} className="">
            <Card className="">
              <CardBody className="p-0">
                {/* <div className="centerContent my-2">
                  <h3>Long term / Short Term</h3>
                </div> */}
                <div className="row m-0 p-0">
                  <div className="col-xxl-6 col-xl-12 col-lg-12 col-md-12 col-12">
                    <PieChartApexTotal
                      dataColors={["#1261A6", "#0FA67B", "#FFB663"]}
                      series={[
                        +summary?.total_long_term_pnl,
                        +summary?.total_short_term_pnl,
                        +summary?.total_speculative_pnl,
                      ]}
                      labels={["Long Term", "Short Term", "Speculative"]}
                      totalLabel={{ label: "Long / Short Term" }}
                    />
                  </div>
                  <div className="centerContent col-xxl-6 col-xl-12 col-lg-12 col-md-12 col-12">
                    <div className=" w-100 ">
                      <div className="d-flex align-items-center justify-content-between border-bottom">
                        <div className=" d-flex align-items-center justify-content-start">
                          <i class="ri-stop-fill align-middle fs-24 text-primary me-1 mb-0"></i>
                          <h4 className="mb-0 fw-medium"> Long Term</h4>
                        </div>
                        <h4 className="mb-0 fw-medium">
                          {" "}
                          {parseFloat(summary?.total_long_term_pnl) < 0 &&(
                               <i class="ms-1 ri-arrow-down-line align-middle text-danger"></i>
                          ) }
                          ₹
                          {parseFloat(
                            summary?.total_long_term_pnl
                          ).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                          }
                          )}
                        </h4>
                      </div>
                      <div className="d-flex align-items-center justify-content-between border-bottom">
                        <div className=" d-flex align-items-center justify-content-start">
                          <i class="ri-stop-fill align-middle fs-24 text-success me-1 mb-0"></i>
                          <h4 className="mb-0 fw-medium"> Short Term</h4>
                        </div>
                        <h4 className="mb-0 fw-medium">
                          {" "}
                          {parseFloat(summary?.total_short_term_pnl) < 0 &&(
                               <i class="ms-1 ri-arrow-down-line align-middle text-danger"></i>
                          ) }
                          ₹
                          {parseFloat(
                            summary?.total_short_term_pnl
                          ).toLocaleString("en-IN",  {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            }
                          
                          )}
                        </h4>
                      </div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className=" d-flex align-items-center justify-content-start">
                          <i
                            class="ri-stop-fill align-middle fs-24 me-1 mb-0"
                            style={{ color: "#FFB663" }}
                          ></i>
                          <h4 className="mb-0 fw-medium"> Speculative</h4>
                        </div>
                        <h4 className="mb-0 fw-medium">
                          {" "}
                          {parseFloat(summary?.total_speculative_pnl) < 0 &&(
                               <i class="ms-1 ri-arrow-down-line align-middle text-danger"></i>
                          ) }
                          ₹
                          {parseFloat(
                            summary?.total_speculative_pnl
                          ).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            }
                          
                          )}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Table Container */}
      {stockType === "close" && (
        <div className="mt-4  d-flex align-items-center justify-content-start">
          <i class="ri-stop-fill align-middle fs-24 text-success me-1 mb-0"></i>
          <h4 className="mb-0"> Closing Stocks</h4>
        </div>
      )}
      <div className="table-responsive table-container">
         {stockType === "open" && (
        <div className=" d-flex align-items-center justify-content-start">
          <i class="ri-stop-fill align-middle fs-24 text-success me-1 mb-0"></i>
          <h4 className="mb-0"> Realised Gain & Loss</h4>
        </div>
      )}
       <div
    ref={tableContainerRef}
    style={{
      maxHeight: '60vh',
      overflow: 'auto',
      position: 'relative'
    }}
  >
    <Table hover className="align-middle table-nowrap table-bordered">
    <thead 
  className={`bg-gl-primary sticky-header ${theadClass}`}
  style={{
    position: 'sticky',
    top: -1,
    zIndex: 2,
    background: 'inherit'
  }}
>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      style={{
                        textAlign: header.column.columnDef.headerTextAlign,
                        cursor: "pointer",
                        whiteSpace: "normal", 
                         padding:"0.3rem",
                         paddingLeft:"0.4rem"
                      }}
                      // onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        {flexRender(
                          header.column.columnDef.Header,
                          header.getContext()
                        )}
                        {/* {isSorted === "asc" ? (
                          <i className="ms-2 bx bx-caret-up"></i>
                        ) : isSorted === "desc" ? (
                          <i className="ms-2 bx bx-caret-down"></i>
                        ) : (
                          <i className="ms-2 bx bxs-sort-alt"></i>
                        )} */}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          
          {tableLoading ? (
            <tr>
              <td
                colSpan={columns?.length}
                className="p-1"
                style={{ textAlign: "center" }}
              >
                <Card className="m-0 text-muted">
                  <CardBody className="centerContent">
                  <CommonLoading style={{
                    maxHeight: "6vh"
                  }} />
                  </CardBody>
                </Card>
              </td>
            </tr>
          ) : (
            <>
           
           <tbody className={tbodyClass}>
  {data?.length > 0 ? (
    rows.length > 0 ? (
      <>
        {paddingTop > 0 && (
          <tr>
            <td colSpan={columns?.length} style={{ height: `${paddingTop}px`, padding: 0 }} />
          </tr>
        )}
        {virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index];
          return (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                const cellValue = cell.getContext().getValue();
                const formattedValue =
                  typeof cellValue === "number" ? formatNumber(cellValue) : cellValue;
                return (
                  <td
                    key={cell.id}
                    className="fs-p-bg fw-medium"
                    style={{
                      textAlign: cell.column.columnDef.bodyTextAlign,
                      width: cell.column.columnDef.size ,
                      minWidth: cell.column.columnDef.minSize ,
                      ...cell.column.columnDef.meta?.style,
                      whiteSpace: "normal",
                      padding: "0.3rem",
                      paddingLeft: "0.4rem"
                    }}
                  >
                    {formattedValue}
                  </td>
                );
              })}
            </tr>
          );
        })}
        {paddingBottom > 0 && (
          <tr>
            <td colSpan={columns?.length} style={{ height: `${paddingBottom}px`, padding: 0 }} />
          </tr>
        )}
      </>
    ) : (
      <tr>
        <td
          colSpan={columns?.length}
          className="p-1"
          style={{ textAlign: "center" }}
        >
          <Card className="m-0 text-muted">
            <CardBody>No Record Found</CardBody>
          </Card>
        </td>
      </tr>
    )
  ) : (
    <tr>
      <td
        colSpan={columns?.length}
        className="p-1"
        style={{ textAlign: "center" }}
      >
        <Card className="m-0 text-muted">
          <CardBody>No Record Found</CardBody>
        </Card>
      </td>
    </tr>
  )}
</tbody>
              {footer && stockType === "open" && (
                <tfoot className="table-footer sticky-footer">
                  <tr className="bg-gl-success">
                    <th colSpan={2} style={{ textAlign: "start" }}>
                      TOTAL
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.open_total_buy ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}></th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.open_total_sell ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th colSpan={5} style={{ textAlign: "end" }}></th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_open_short_term_pnl ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_open_long_term_pnl ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_open_speculative_pnl ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.open_total_pnl ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.open_total_open_pos_val ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.open_total_close_pos_val ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                  </tr>
                </tfoot>
              )}
              {footer && stockType === "close" && (
                <tfoot className="table-footer sticky-footer">
                  <tr className="bg-gl-success">
                    <th colSpan={2} style={{ textAlign: "start" }}>
                      TOTAL
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.close_total_buy ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}></th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.close_total_sell ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th colSpan={4} style={{ textAlign: "end" }}></th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_close_change_percentage ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_close_short_term_pnl ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_close_long_term_pnl ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_close_speculative_pnl ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.close_total_pnl ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.close_total_open_pos_val ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_close_closing_value ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                  </tr>
                </tfoot>
              )}
            </>
          )}
        </Table>
      </div>
      </div>
    </Fragment>
  );
};

export default TableContainerDateRangeFilter4;
